import { erc20 as erc20ABI } from '@jdiyield/abi'
import { chainData, ChainID } from '@jdiyield/constants'
import { Chain, MyAsset, MyAssetLP, PriceList, Token } from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import { calculateTokenBalance } from './calculate'
import getNodeUrl from './rpcRandom'
import { allBalancesOf, findToken, makeBatchRequest } from './utils'

type Props = {
  web3?: Web3
  walletAddress: string
  tokens: Token[]
  coinList: PriceList
  chainID: ChainID
}
export const getAssets = async ({
  web3: web3Prop,
  walletAddress,
  tokens,
  coinList,
  chainID
}: Props): Promise<MyAsset[]> => {
  try {
    // const priceList = await getBscPricesFromTokenList(tokenList)
    const chainName = chainData[chainID].chainName as Chain
    const restTokens = tokens.filter(
      (token) => token.chain === chainName //&& !Object.values(excludeTokens).includes(token.address)
    )

    let web3 = web3Prop
    if (!web3Prop) {
      const provider = new Web3.providers.HttpProvider(getNodeUrl(chainID), {
        timeout: 120000
      })
      web3 = new Web3(provider)
    }

    // const balanceBNB = await balanceOf(walletAddress, excludeTokens.BNB)
    const balancesTokens = await allBalancesOf(
      web3,
      walletAddress,
      restTokens.map(({ address }) => address),
      chainID
    )
    const result = await Promise.all(
      balancesTokens.map(async ({ contract, balance }) => {
        const token = findToken(tokens, contract)
        const value = new BigNumber(balance).dividedBy(Math.pow(10, token?.decimals || 18)).toNumber()
        // const { value: valueUSD, usdPerCoin: ratePriceUSD } = getTokenUSD({ address: contract, value })
        const { worth: valueUSD, usdPerCoin: ratePriceUSD } = await calculateTokenBalance(
          web3,
          contract,
          balance,
          tokens,
          coinList
        )

        return {
          currency: {
            address: token?.address,
            name: token?.symbol,
            symbol: token?.symbol,
            decimals: token?.decimals || 18
          },
          logo: token?.logo ?? 'https://bscscan.com/images/main/empty-token.png',
          value: !isNaN(value) ? value : 0,
          valueUSD: !isNaN(valueUSD) ? valueUSD : 0,
          ratePriceUSD: !isNaN(ratePriceUSD) ? ratePriceUSD : 0
        }
      })
    )
    return result.filter(({ value }) => value !== 0)
  } catch (e) {
    console.log('getAssets', e)
    return []
  }
}

export const getLpTokenAssets = async ({
  walletAddress,
  lpTokens,
  chainID,
  web3Prop
}: {
  walletAddress: string
  lpTokens: any[]
  chainID: ChainID
  web3Prop?: Web3
}): Promise<MyAssetLP[]> => {
  try {
    const chainName = chainData[chainID].chainName as Chain

    const web3 = web3Prop || new Web3(getNodeUrl(chainID))

    let lpTokenContract = lpTokens.map(({ address }) => address)
    const tokens = await allBalancesOf(web3, walletAddress, lpTokenContract, chainID)
    lpTokenContract = null

    const balancesTokens = tokens.filter((token, index) => +token.balance > 0.001 && index !== 0)
    if (balancesTokens.length === 0) return []

    const tokensInfo = await Promise.all(
      balancesTokens.map(async ({ contract }) => {
        const tokenInfo = findToken(lpTokens, contract)
        if (!tokenInfo.symbol || !tokenInfo.decimals) {
          const tokenContract = new web3.eth.Contract(erc20ABI as any, contract)
          const [symbol, decimals] = await makeBatchRequest(web3, [
            tokenContract.methods.symbol().call,
            tokenContract.methods.decimals().call
          ])

          return { ...tokenInfo, symbol, decimals }
        } else {
          return tokenInfo
        }
      })
    )

    const result = balancesTokens.map(({ balance }, i) => {
      const value = new BigNumber(balance).dividedBy(Math.pow(10, tokensInfo[i].decimals || 18)).toNumber()

      return {
        token: {
          address: tokensInfo[i].address,
          name: tokensInfo[i].symbol,
          symbol: tokensInfo[i].symbol,
          decimals: tokensInfo[i].decimals || 18
        },
        logo: tokensInfo[i].logo ?? 'https://bscscan.com/images/main/empty-token.png',
        value: !isNaN(value) ? value : 0,
        balance: +balance,
        chain: chainName
      }
    }) as MyAssetLP[]

    return result
  } catch {
    return []
  }
}
